import {InvoiceBillTo} from './ExportInvoiceModal.type';
import {XeroDefaultTaxRate} from './ExportDefaultTaxRate.display';

/*
 * The client's stored QuickBooks default tax code (graphql `Client.qboDefaultTaxRate`, T4-188). Set only
 * while the linked tax code is ACTIVE; `organisation` is the realm it was read from.
 */
export type QuickbooksDefaultTaxRate = {name: string | null; effectiveRate: number | null; organisation: string | null};

/** One option in the legacy-path Client dropdown, as ExportInvoiceButton builds it. */
export type QuickbooksCustomerOption = {
	value: string;
	label: string;
	realmId: string | null;
	/** Absent (never null) when the client has none, so options without one keep today's shape. */
	defaultTaxRate?: QuickbooksDefaultTaxRate;
};

/** One option in the legacy-path Xero Client dropdown, as ExportInvoiceButton builds it. */
export type XeroClientOption = {
	value: string;
	label: string;
	xeroContactId: string;
	/** The client's Xero default tax rate, for the header (T4-197). Absent with the Xero improvements flag off. */
	xeroDefaultTaxRate?: XeroDefaultTaxRate | null;
};

/*
 * Where an export goes. The pathways window knows it from the bill-to; the legacy window from the
 * client chosen in its header. Every "which integration / what input" question the modal asks goes
 * through this, so the two windows share one component.
 */
export type ExportTarget =
	| {kind: 'BILL_TO'; billTo: InvoiceBillTo}
	| {
			kind: 'CLIENT';
			integration: 'QUICKBOOKS';
			/** Null until a client is chosen. */
			clientId: string | null;
			/** Null when the client carries no realm: a single-connection company. */
			realmId: string | null;
	  }
	| {
			kind: 'CLIENT';
			integration: 'XERO';
			/** Null until a client is chosen. */
			clientId: string | null;
			/** The chosen client's Xero contact. Null until a client is chosen. */
			xeroContactId: string | null;
			/** The organisation that contact belongs to. Null until xero/contacts answers with it. */
			tenantId: string | null;
	  };

/** The targets a Xero export can go to: the pathways bill-to, or the legacy window's Xero client. */
export type XeroExportTarget =
	| Extract<ExportTarget, {kind: 'BILL_TO'}>
	| Extract<ExportTarget, {kind: 'CLIENT'; integration: 'XERO'}>;

export const getTargetIntegration = (target: ExportTarget | null): string | null => {
	if (!target) return null;
	return target.kind === 'BILL_TO' ? target.billTo.billFrom?.integration ?? null : target.integration;
};
